import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings } from "lucide-react";

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    functional: true, // Always required
    analytics: false,
    tracking: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Small delay to prevent flash
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (type) => {
    let consentData = {
      functional: true,
      analytics: false,
      tracking: false,
      timestamp: new Date().toISOString(),
    };

    if (type === "all") {
      consentData.analytics = true;
      consentData.tracking = true;
    } else if (type === "essential") {
      // Only functional cookies
    } else if (type === "custom") {
      consentData = { ...preferences, timestamp: new Date().toISOString() };
    }

    localStorage.setItem("cookieConsent", JSON.stringify(consentData));
    setShowBanner(false);
    setShowPreferences(false);

    // Here you would initialize analytics/tracking based on consent
    if (consentData.analytics) {
      // Initialize analytics
      console.log("Analytics cookies enabled");
    }
    if (consentData.tracking) {
      // Initialize tracking
      console.log("Tracking cookies enabled");
    }
  };

  const handlePreferenceChange = (key) => {
    if (key === "functional") return; // Cannot disable functional cookies
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      {showBanner && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-[99]"
            onClick={() => {}}
          />

          {/* Cookie Banner */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
            data-testid="cookie-consent-banner"
          >
            <div className="max-w-4xl mx-auto bg-white shadow-2xl border border-gray-100 rounded-sm overflow-hidden">
              {!showPreferences ? (
                /* Main Banner */
                <div className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-cta/20 rounded-sm flex-shrink-0">
                      <Cookie className="w-6 h-6 text-cta" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-xl text-navy mb-2">
                        Wij gebruiken cookies
                      </h3>
                      <p className="text-navy/70 text-sm leading-relaxed mb-4">
                        Deze website gebruikt cookies om je ervaring te verbeteren, 
                        het websitegebruik te analyseren en voor marketingdoeleinden. 
                        Tracking cookies worden pas geplaatst na jouw toestemming.
                      </p>
                      <Link
                        to="/cookiebeleid"
                        className="text-berry text-sm font-medium hover:underline"
                        data-testid="cookie-policy-link"
                      >
                        Lees ons cookiebeleid →
                      </Link>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <button
                      onClick={() => saveConsent("all")}
                      data-testid="cookie-accept-all"
                      className="bg-burgundy text-white px-6 py-3 text-sm font-semibold hover:bg-navy transition-colors"
                    >
                      Alle cookies accepteren
                    </button>
                    <button
                      onClick={() => saveConsent("essential")}
                      data-testid="cookie-reject-nonessential"
                      className="border border-burgundy text-burgundy px-6 py-3 text-sm font-semibold hover:bg-burgundy hover:text-white transition-colors"
                    >
                      Alleen essentiële cookies
                    </button>
                    <button
                      onClick={() => setShowPreferences(true)}
                      data-testid="cookie-manage-preferences"
                      className="flex items-center justify-center gap-2 text-navy/70 px-6 py-3 text-sm font-medium hover:text-berry transition-colors"
                    >
                      <Settings size={16} />
                      Voorkeuren beheren
                    </button>
                  </div>
                </div>
              ) : (
                /* Preferences Panel */
                <div className="p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-serif text-xl text-navy">
                      Cookie voorkeuren
                    </h3>
                    <button
                      onClick={() => setShowPreferences(false)}
                      className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
                      aria-label="Sluiten"
                    >
                      <X size={20} className="text-navy/70" />
                    </button>
                  </div>

                  <div className="space-y-4 mb-6">
                    {/* Functional Cookies */}
                    <div className="flex items-start justify-between p-4 bg-mist rounded-sm">
                      <div className="flex-1 pr-4">
                        <h4 className="font-semibold text-navy mb-1">
                          Functionele cookies
                        </h4>
                        <p className="text-sm text-navy/70">
                          Noodzakelijk voor de werking van de website. Kunnen niet worden uitgeschakeld.
                        </p>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-cta font-medium bg-cta/20 px-2 py-1 rounded">
                          Altijd aan
                        </span>
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="flex items-start justify-between p-4 bg-mist rounded-sm">
                      <div className="flex-1 pr-4">
                        <h4 className="font-semibold text-navy mb-1">
                          Analytische cookies
                        </h4>
                        <p className="text-sm text-navy/70">
                          Helpen ons te begrijpen hoe bezoekers de website gebruiken.
                        </p>
                      </div>
                      <button
                        onClick={() => handlePreferenceChange("analytics")}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          preferences.analytics ? "bg-burgundy" : "bg-gray-300"
                        }`}
                        data-testid="toggle-analytics"
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.analytics ? "right-1" : "left-1"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Tracking Cookies */}
                    <div className="flex items-start justify-between p-4 bg-mist rounded-sm">
                      <div className="flex-1 pr-4">
                        <h4 className="font-semibold text-navy mb-1">
                          Tracking cookies
                        </h4>
                        <p className="text-sm text-navy/70">
                          Worden gebruikt voor marketingdoeleinden en gepersonaliseerde advertenties.
                        </p>
                      </div>
                      <button
                        onClick={() => handlePreferenceChange("tracking")}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          preferences.tracking ? "bg-burgundy" : "bg-gray-300"
                        }`}
                        data-testid="toggle-tracking"
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            preferences.tracking ? "right-1" : "left-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => saveConsent("custom")}
                      data-testid="cookie-save-preferences"
                      className="bg-burgundy text-white px-6 py-3 text-sm font-semibold hover:bg-navy transition-colors"
                    >
                      Voorkeuren opslaan
                    </button>
                    <button
                      onClick={() => saveConsent("all")}
                      className="border border-burgundy text-burgundy px-6 py-3 text-sm font-semibold hover:bg-burgundy hover:text-white transition-colors"
                    >
                      Alle cookies accepteren
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
